import { forkJoin as observableForkJoin } from 'rxjs';
import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { LoadingPanelService, LoadingPanelSettings } from '../../shared/loading-panel/loading-panel.service';
import { StatusEventSearchObject } from '../../tracking/models/status-event-search-object';
import { DeveloperService } from '../../developer/services/developer.service'
import { StateService } from '../../shared/state-management/state.service';
import { DatetimePickerComponent } from '../../shared/datetime-picker/datetime-picker.component';
import { Guid } from '../../shared/utils/guid';
import { Router } from '@angular/router';
import { StatusEventSubscriptionStatusTypeEntity } from '../../developer/models/status-event-subscription-status-type';
import { StatusEventSubscription } from '../../developer/models/status-event-subscription';
import { FormsModule } from '@angular/forms';

import { SvgIconComponent } from '../../shared/icons/svg-icon.component';


@Component({
    templateUrl: './tracking-search-status-events.component.html',
    styleUrls: ['./tracking-search-status-events.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, DatetimePickerComponent, SvgIconComponent]
})

export class TrackingSearchStatusEventComponent implements OnInit {

    constructor(
        private loadingPanelService: LoadingPanelService,
        private developerservice: DeveloperService,
        private router: Router,
        private stateService: StateService) { }

    statusEventSubscriptions: StatusEventSubscription[] = [];
    statusEventSubscriptionStatusTypes: StatusEventSubscriptionStatusTypeEntity[] = [];
    searchObject: StatusEventSearchObject = { StatusEventSubscriptionId: null, StatusEventSubscriptionStatusTypeId: null, DocumentId: null, InterchangeInId: null, FromDate: null, ToDate: null, SearchId: Guid.empty } as StatusEventSearchObject;
    readonly stateKey: string = 'tracking-search-status-event-state';

    @ViewChild('datepickerFrom', { static: true }) private datepickerFrom: DatetimePickerComponent;
    @ViewChild('datepickerTo', { static: true }) private datepickerTo: DatetimePickerComponent;

    ngOnInit(): void {
        this.loadingPanelService.showLoadingPanel(new LoadingPanelSettings('Initialize data...'));

        let statusEventSubscriptionPromise = this.developerservice.getStatusEventSubscriptions().toPromise().then((result) => {
            result.sort((x1, x2) => x1.Name.localeCompare(x2.Name));
            this.statusEventSubscriptions = result;
        });

        let statusEventSubscriptionStatusTypesPromise = this.developerservice.getStatusEventSubscriptionStatusTypes().then((result) => {
            result.sort((x1, x2) => x1.Name.localeCompare(x2.Name));
            this.statusEventSubscriptionStatusTypes = result;
        });

        let promises = [
            statusEventSubscriptionPromise,
            statusEventSubscriptionStatusTypesPromise
        ];

        observableForkJoin(promises).subscribe(() => {
            this.loadingPanelService.hideLoadingPanel();
            if (!this.setStateData()) {
                this.clearSearchObject();
            }
        });
    }

    setStateData(): boolean {
        let stateData = this.stateService.getState(this.stateKey);
        if (stateData) {
            this.clearSearchObject();
            // To keep the "default" values from the reset-function - merge the two search-objects.
            Object.assign(this.searchObject, stateData.searchObject);

            if (this.searchObject.FromDate) {
                this.datepickerFrom.setSelectedDate(new Date(this.searchObject.FromDate));
            }
            if (this.searchObject.ToDate) {
                this.datepickerTo.setSelectedDate(new Date(this.searchObject.ToDate));
            }
            return true;
        }
        return false;
    }

    clearSearchObject() {
        this.searchObject = { StatusEventSubscriptionId: null, StatusEventSubscriptionStatusTypeId: null, DocumentId: null, InterchangeInId: null, FromDate: null, ToDate: null, SearchId: Guid.empty } as StatusEventSearchObject;
    }

    dateFromChanged(date: Date) {
        this.searchObject.FromDate = date ? date.toISOString() : null;
    }

    dateToChanged(date: Date) {
        this.searchObject.ToDate = date ? date.toISOString() : null;
    }

    search() {
        // Clean the search-object to prevent ugly unnecessary null-values in url-parameters
        let tempSearchObject: any = {};
        for (var searchProperty in this.searchObject) {
            if (this.searchObject[searchProperty] === false || this.searchObject[searchProperty]) { // Include booleans with false value and all other properties with a value
                tempSearchObject[searchProperty] = this.searchObject[searchProperty];
            }
        }

        let stateData: any = {};
        stateData.searchObject = tempSearchObject;

        stateData.searchObject.SearchId = Guid.newGuid(); // By setting this unique identifier we force the search-result page to reload the data.
        this.stateService.setState(this.stateKey, stateData);
        this.router.navigate(['tracking/searchstatusevents/result', tempSearchObject]);
    }

    reset(resetState: boolean = true) {
        if (resetState) {
            this.stateService.removeState(this.stateKey);
        }

        this.clearSearchObject();

        this.datepickerFrom.reset();
        this.datepickerTo.reset();
    }
}
